import { Alert, Button, Card } from '@rooming/ui';
import { formatInZone } from '@rooming/web-core';
import type { SwapRequest } from '../api';

const RESOLVED: Record<
  Exclude<SwapRequest['status'], 'REQUESTED'>,
  { tone: 'success' | 'info' | 'warning'; text: string }
> = {
  EXECUTED: { tone: 'success', text: 'Intercambio realizado' },
  REJECTED: { tone: 'info', text: 'No aceptaron el intercambio' },
  CANCELLED: { tone: 'info', text: 'Solicitud cancelada' },
  EXPIRED: {
    tone: 'warning',
    text: 'Tu solicitud venció: pasaron 24 horas sin respuesta. Si todavía querés el cambio, enviá una nueva',
  },
  INVALIDATED: { tone: 'warning', text: 'Alguna de las plazas cambió: la solicitud quedó sin efecto' },
};

/**
 * The passenger's swap requests in this stay (CONCURRENCY_AND_TRANSACTION_DESIGN.md §9): what they are being asked,
 * what they asked, and how recent requests ended. Both sides see who the other is (DR-371), so they can talk first.
 */
export function SwapPanel({
  swaps,
  busy,
  timeZone,
  onAnswer,
}: {
  readonly swaps: readonly SwapRequest[];
  readonly busy: boolean;
  /** Expiry is shown in the hotel's time, like every other date of the stay. */
  readonly timeZone: string;
  readonly onAnswer: (swap: SwapRequest, answer: 'acceptance' | 'rejection' | 'cancellation') => void;
}) {
  if (swaps.length === 0) return null;
  const pending = swaps.filter((swap) => swap.status === 'REQUESTED');
  const resolved = swaps.filter((swap) => swap.status !== 'REQUESTED').slice(0, 3);
  return (
    <section aria-label="Intercambios" className="flex flex-col gap-3">
      <h2 className="text-lg font-semibold">Intercambios</h2>
      {pending.map((swap) =>
        swap.direction === 'INCOMING' ? (
          <Card key={swap.id} className="flex flex-col gap-3 border-brand-600">
            <p className="text-base">
              <strong>{swap.otherName}</strong>, de <strong>{swap.otherRoom}</strong>, te propone intercambiar: vos
              pasarías de <strong>{swap.myRoom}</strong> a <strong>{swap.otherRoom}</strong>.
            </p>
            <p className="text-sm text-muted">
              Antes de responder, hablá con {swap.otherName} y con tus compañeros de habitación.
            </p>
            <p className="text-sm text-muted">Podés responder hasta el {formatInZone(swap.expiresAt, timeZone)}.</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                loading={busy}
                aria-label={`Aceptar el intercambio a ${swap.otherRoom}`}
                onClick={() => {
                  onAnswer(swap, 'acceptance');
                }}
              >
                Aceptar
              </Button>
              <Button
                variant="secondary"
                disabled={busy}
                aria-label={`Rechazar el intercambio a ${swap.otherRoom}`}
                onClick={() => {
                  onAnswer(swap, 'rejection');
                }}
              >
                Rechazar
              </Button>
            </div>
          </Card>
        ) : (
          <Card key={swap.id} className="flex flex-col gap-3">
            <p className="text-base">
              Pediste intercambiar tu plaza de <strong>{swap.myRoom}</strong> por la de{' '}
              <strong>{swap.otherName}</strong> en <strong>{swap.otherRoom}</strong>. Esperando respuesta.
            </p>
            <p className="text-sm text-muted">Vence el {formatInZone(swap.expiresAt, timeZone)}.</p>
            <div>
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  onAnswer(swap, 'cancellation');
                }}
              >
                Cancelar mi solicitud
              </Button>
            </div>
          </Card>
        ),
      )}
      {resolved.map((swap) => {
        const outcome = RESOLVED[swap.status as keyof typeof RESOLVED];
        // Whoever was asked sees an expired request as what it is for them: one they did not answer in time.
        const text =
          swap.status === 'EXPIRED' && swap.direction === 'INCOMING'
            ? 'Una solicitud de intercambio que te hicieron venció sin respuesta'
            : outcome.text;
        return (
          <Alert key={swap.id} tone={outcome.tone}>
            {text} ({swap.myRoom} ↔ {swap.otherRoom}, con {swap.otherName}).
          </Alert>
        );
      })}
    </section>
  );
}
